import { Grid, Typography } from '@mui/material'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import {
  SocioeducationMeasureIdentificationInit,
  SocioeducationMeasureIdentificationSchema,
  SocioeducationMeasureIdentificationType,
} from 'types/socioeducation-measure-identification.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useOutletContext } from 'react-router'
import React, { Dispatch, MutableRefObject, SetStateAction, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import FormInput from 'components/FormInput'
import { AddressList } from 'pages/SocioeducationMeasureEdit/components/AddressList'
import FormControlRadioBoolean from 'components/FormControlRadioBoolean'
import { PhoneList } from 'pages/SocioeducationMeasureEdit/components/PhoneList'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { CreasResponse } from 'types/creas.type'
import { useCreasUsuario } from 'pages/SearchCreas/hook/creas-usuario.hook'
import { useMeioAbertoIdentificacao } from 'hooks/meio-aberto-identificacao.hook'
import { DocumentList } from 'pages/SocioeducationMeasureEdit/components/DocumentList'
import { useInputSelectIdentification } from 'pages/CreateHearing/components/StepIdentification/hooks/select-option.hook'
import FormInputDatePicker from 'components/FormInputDatePicker'
import { ItemOption } from 'components/InputSelect'
import { addressInit, NaturalPersonAddressInit } from 'types/address.type'
import { calculateAge } from 'utils/date.util'
import { useAuthContext } from 'contexts/auth.context'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import { disable } from 'workbox-navigation-preload'
import { convertNumbersOnly } from 'utils/mask.util'

type OutletContextType = {
  currentStep: number
  pessoaFisicaId?: number
  formRefs: MutableRefObject<Record<number, { submit: () => Promise<void> | void }>>
  setPessoaFisicaId: Dispatch<SetStateAction<number | null | undefined>>
}

export const SocioeducationMeasureIdentificationEdit = () => {
  const { id } = useParams()
  const { handleHasPermission } = useAuthContext()

  const { currentStep, formRefs, setPessoaFisicaId } = useOutletContext<OutletContextType>()
  const methods = useForm<SocioeducationMeasureIdentificationType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(SocioeducationMeasureIdentificationSchema),
    defaultValues: SocioeducationMeasureIdentificationInit,
  })

  const { creas } = useCreasUsuario()
  const { identificacao, handleSalvar: handleSalvarMeioAberto } = useMeioAbertoIdentificacao(id)
  const { genderOptions, skinOptions, docTypeOptions } = useInputSelectIdentification()

  const {
    handleSubmit,
    clearErrors,
    control,
    reset,
    watch,
    setError,
    setValue,
    formState: { isDirty, dirtyFields },
  } = methods

  const doNotHasEditPermission = !handleHasPermission(Funcionalidades.meioAberto.editar)

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const temFilhos: boolean | null | undefined = watch('temFilhos')
  const dataNascimentoWatch = watch('pessoaFisica.dataNascimento')
  const creasWatch = watch('creas')
  const quantidadeFilhosWatch = watch('quantidadeFilhos')

  useEffect(() => {
    if (quantidadeFilhosWatch) {
      setValue('quantidadeFilhos', convertNumbersOnly(quantidadeFilhosWatch))
    }
  }, [quantidadeFilhosWatch])

  useEffect(() => {
    if (!temFilhos) {
      setValue('quantidadeFilhos', null)
    }
  }, [temFilhos])

  useEffect(() => {
    if (identificacao) {
      setPessoaFisicaId(identificacao.pessoaFisica.id)
      reset(identificacao)
    }
  }, [identificacao])

  useEffect(() => {
    if (dataNascimentoWatch) {
      setValue('pessoaFisica.idade', calculateAge(dataNascimentoWatch))
    }
  }, [dataNascimentoWatch])

  const handleSalvar = async (data?: SocioeducationMeasureIdentificationType) => {
    await handleSalvarMeioAberto(data)
    return Promise.resolve()
  }

  const onError = (errors: FieldErrors<SocioeducationMeasureIdentificationType>) => {
    const recursiveSetError = (errors: any, parentPath = '') => {
      clearErrors()
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }

    recursiveSetError(errors)
    return Promise.reject(errors)
  }

  formRefs.current[currentStep] = {
    submit: async () => {
      if (isDirty && hasDirtyFields && handleHasPermission(Funcionalidades.meioAberto.editar)) {
        await handleSubmit(handleSalvar, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSalvar, onError)}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'body1'} color={'secondary'}>
              <strong>IDENTIFICAÇÃO DO ADOLESCENTE/JOVEM</strong>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <form>
              <Grid container item xs={12} spacing={2}>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'pessoaFisica.nomeSocial'}
                    label={'Nome social'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'pessoaFisica.nome'}
                    label={'Nome'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInputDatePicker
                    name={'pessoaFisica.dataNascimento'}
                    label={'Data de nascimento'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'pessoaFisica.idade'}
                    label={'Idade'}
                    control={control}
                    disabled
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInputAutoComplete
                    label={'Gênero:'}
                    name={'pessoaFisica.genero.id'}
                    control={control}
                    options={genderOptions}
                    disabled={doNotHasEditPermission}
                    getOptionLabel={(option) => option.label}
                    getOptionById={(id: string | null, options: ItemOption[]) =>
                      options.find((option) => Number(option.value) === Number(id)) || null
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChangeValue={(option) => Number(option?.value) || null}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInputAutoComplete
                    label={'Cor:'}
                    name={'pessoaFisica.corPele.id'}
                    control={control}
                    options={skinOptions}
                    disabled={doNotHasEditPermission}
                    getOptionLabel={(option) => option.label}
                    getOptionById={(id: string | null, options: ItemOption[]) =>
                      options.find((option) => Number(option.value) === Number(id)) || null
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChangeValue={(option) => Number(option?.value) || null}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'pessoaFisica.nomeMae'}
                    label={'Nome da mãe'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'pessoaFisica.nomePai'}
                    label={'Nome do pai'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'nomeResponsavelLegal'}
                    label={'Responsável legal'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'religiao'}
                    label={'Religião'}
                    control={control}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputAutoComplete
                    label={'CREAS:'}
                    name={'creas'}
                    control={control}
                    options={creas}
                    disabled={doNotHasEditPermission}
                    getOptionLabel={(option) => option.nome}
                    getOptionById={(option: CreasResponse | null, options: CreasResponse[]) =>
                      creasWatch && creasWatch.id && creasWatch.nome
                        ? ({
                            id: creasWatch.id,
                            nome: creasWatch.nome,
                            abrangencia: [],
                            ativo: '',
                            diretor: '',
                            email: '',
                            endereco: addressInit,
                            telefones: [],
                          } as CreasResponse)
                        : null
                    }
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChangeValue={(option) => option || null}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography color={'secondary'} variant={'body1'}>
                    <strong>DOCUMENTOS</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <DocumentList typeOptions={docTypeOptions} disabled={doNotHasEditPermission} />
                </Grid>
                <Grid item xs={12}>
                  <Typography color={'secondary'} variant={'body1'}>
                    <strong>ENDEREÇOS</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <AddressList
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    fieldArrayName={'pessoaFisica.enderecos'}
                    initialValuesFieldName={'pessoaFisica.enderecos'}
                    initialValues={[NaturalPersonAddressInit]}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography color={'secondary'} variant={'body1'}>
                    <strong>TELEFONES</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <PhoneList disabled={doNotHasEditPermission} />
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item md={3} xs={12}>
                    <FormControlRadioBoolean
                      control={control}
                      name='temFilhos'
                      label='Tem filhos?'
                      options={[
                        {
                          label: 'Sim',
                          value: true,
                        },
                        {
                          label: 'Não',
                          value: false,
                        },
                      ]}
                      disabled={doNotHasEditPermission}
                    />
                  </Grid>
                  {temFilhos && (
                    <Grid
                      item
                      md={3}
                      xs={12}
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems={'center'}
                    >
                      <FormInput
                        name={'quantidadeFilhos'}
                        label={'Quantidade'}
                        control={control}
                        disabled={doNotHasEditPermission}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControlRadioBoolean
                    control={control}
                    name='emGestacao'
                    label='Em gestação?'
                    options={[
                      {
                        label: 'Sim',
                        value: true,
                      },
                      {
                        label: 'Não',
                        value: false,
                      },
                    ]}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
